// Reusable button component for starting free trial checkout flow
import { pushEvent } from "@/lib/gtm";
import { Button } from "@/src/components/ui/Button";
import { CountryStripePriceInfo } from "@/src/features/pricing/types";
import clsx from "clsx";
import posthog from "posthog-js";

interface StartFreeTrialButtonProps {
  selectedPlan?: {
    price: CountryStripePriceInfo;
    planType: "yearly" | "monthly";
  } | null;
  defaultPrice?: CountryStripePriceInfo;
  hasHadATrial: boolean;
  couponCode?: string;
  currentUser: any;
  currentStepIndex: number;
  logOnboardingStep: (
    currentStepIndex: number,
    currentStepKey: string,
    currentUser: any,
    otherObjective: string,
    otherText: string,
    otherCategory: string
  ) => void;
  otherObjective: string;
  otherText: string;
  otherCategory: string;
  onPlanSelected?: () => void;
  onComplete?: () => void;
  className?: string;
  size?: "sm" | "lg" | "default" | "icon";
  variant?:
    | "default"
    | "destructive"
    | "positive"
    | "violet"
    | "outline"
    | "secondary"
    | "ghost"
    | "link";
}

export const StartFreeTrialButton = ({
  selectedPlan,
  defaultPrice,
  hasHadATrial,
  couponCode,
  currentUser,
  currentStepIndex,
  logOnboardingStep,
  otherObjective,
  otherText,
  otherCategory,
  onPlanSelected,
  onComplete,
  className,
  size = "lg",
  variant = "default",
}: StartFreeTrialButtonProps) => {
  const handleStartFreeTrial = () => {
    if (!defaultPrice || !currentUser) return;

    const plan = selectedPlan || {
      price: defaultPrice,
      planType: "yearly" as "yearly" | "monthly",
    };

    // Track the selection
    posthog.capture("onboarding_plan_selected", {
      userId: currentUser.id,
      plan: plan.planType,
      cost: plan.price.unit_amount,
      variant: "default_yearly",
    });

    // Set checkout data
    localStorage.setItem("checkout_frequency", plan.planType);
    localStorage.setItem("checkout_trial", !hasHadATrial ? "true" : "false");
    localStorage.setItem(
      "checkout_advertised_cost",
      plan.price.unit_amount?.toString() ?? "0"
    );

    pushEvent("checkout_start", {
      frequency: plan.planType,
      advertised_cost: plan.price.unit_amount,
      trial: !hasHadATrial,
    });

    // Log onboarding step
    logOnboardingStep(
      currentStepIndex,
      "onboardingChooseYourPlan",
      currentUser,
      otherObjective,
      otherText,
      otherCategory
    );

    // Submit to checkout
    const form = document.createElement("form");
    form.action = "/api/plus/checkout";
    form.method = "POST";
    form.setAttribute("data-rewardful", "true");

    const inputs = [
      { name: "priceId", value: plan.price.id },
      {
        name: "nGNndCdFpnDazku",
        value: !hasHadATrial ? "scUmYobrToNvMkY" : "WrmUyxZMkBbxBrU",
      },
      { name: "email", value: currentUser.email ?? "" },
    ];

    if (couponCode) {
      inputs.push({ name: "coupon", value: couponCode });
    }

    inputs.forEach(({ name, value }) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);

    onPlanSelected?.();
    onComplete?.();
  };

  return (
    <Button
      onClick={() => {
        handleStartFreeTrial();
        return null;
      }}
      size={size}
      variant={variant}
      className={clsx("w-full", className)}
    >
      Start your FREE trial
    </Button>
  );
};
